/**
 * Throttle una función para que se ejecute máximo una vez cada {limit} ms
 * @param func - Función a throttlear
 * @param limit - Tiempo mínimo entre ejecuciones en ms
 * @returns Función throttleada
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => ReturnType<T> {
  let inThrottle: boolean = false;
  let lastResult: ReturnType<T> | undefined;

  return function (this: unknown, ...args: Parameters<T>): ReturnType<T> {
    if (!inThrottle) {
      inThrottle = true;
      lastResult = func.apply(this, args) as ReturnType<T>;

      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }

    return lastResult as ReturnType<T>;
  };
}

/**
 * Versión mejorada de throttle con trailing call
 * Garantiza que la última llamada también se ejecute
 */
export function throttleWithTrailing<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  let lastArgs: Parameters<T> | null = null;
  let timeoutId: NodeJS.Timeout | null = null;

  return function (this: unknown, ...args: Parameters<T>) {
    lastArgs = args;

    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      lastArgs = null;

      timeoutId = setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          func.apply(this, lastArgs);
          lastArgs = null;
        }
      }, limit);
    }
  };
}
